import { useState, useEffect } from "react";
import StepShell from "@/components/session/StepShell";
import QuestionnaireStep from "@/components/session/QuestionnaireStep";
import SmartGoalsStep from "@/components/session/SmartGoalsStep";
import SmartQuizStep from "@/components/session/SmartQuizStep";
import AbcTheoryStep from "@/components/session/AbcTheoryStep";
import AbcPhysiologyStep from "@/components/session/AbcPhysiologyStep";
import AbcReframeStep from "@/components/session/AbcReframeStep";
import AbcIdentifyStep from "@/components/session/AbcIdentifyStep";
import SummaryStep from "@/components/session/SummaryStep";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ALL_QUESTIONNAIRES } from "@/data/questionnaires";
import { saveProgress, getSessionProgress } from "@/lib/api";
import { ChevronRight, HeartHandshake, ShieldCheck, Clock, ClipboardList, Target, BookOpen, Repeat, Search, CheckCircle2, RotateCcw, ClipboardCheck, Sparkles, Brain, Activity } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

// Session 1 organiza sus pasos en 3 apartados:
//  Apartado 1 · Tests iniciales  (6 cuestionarios)
//  Apartado 2 · Objetivos        (SMART goals + autoevaluación)
//  Apartado 3 · Modelo ABC       (teoría + reformular + identificar)
const SECTIONS = {
  tests:     { id: "tests", number: 1, label: "Tests iniciales" },
  objetivos: { id: "objetivos", number: 2, label: "Objetivos" },
  abc:       { id: "abc", number: 3, label: "Modelo ABC" },
};

const STEP_META = [
  { id: "welcome", label: "Bienvenida", icon: HeartHandshake },
  { id: "intro-tests", label: "Apartado 1", icon: ClipboardCheck, section: SECTIONS.tests },
  ...ALL_QUESTIONNAIRES.map((q) => ({ id: q.kind, label: q.title.split(" · ")[0], icon: ClipboardList, section: SECTIONS.tests })),
  { id: "intro-objetivos", label: "Apartado 2", icon: Sparkles, section: SECTIONS.objetivos },
  { id: "smart-goals", label: "Objetivos SMART", icon: Target, section: SECTIONS.objetivos },
  { id: "smart-quiz", label: "Autoevaluación SMART", icon: CheckCircle2, section: SECTIONS.objetivos },
  { id: "intro-abc", label: "Apartado 3", icon: Brain, section: SECTIONS.abc },
  { id: "abc-theory", label: "Modelo ABC", icon: BookOpen, section: SECTIONS.abc },
  { id: "abc-physiology", label: "Cuerpo y mente", icon: Activity, section: SECTIONS.abc },
  { id: "abc-reframe", label: "Reformular", icon: Repeat, section: SECTIONS.abc },
  { id: "abc-identify", label: "Identificar", icon: Search, section: SECTIONS.abc },
  { id: "summary", label: "Resumen", icon: CheckCircle2 },
];

export default function Session1() {
  const [step, setStep] = useState(0);
  const [resumeAvailable, setResumeAvailable] = useState(null);
  const navigate = useNavigate();
  const total = STEP_META.length;

  useEffect(() => {
    getSessionProgress(1).then(d => {
      if (d?.current_step && d.current_step > 0 && d.current_step < total) {
        setResumeAvailable(d.current_step);
      }
    }).catch(() => {});
  }, [total]);

  useEffect(() => {
    if (step === 0) return;
    saveProgress(1, "_current_step", false, { step_index: step }).catch(() => {});
  }, [step]);

  const next = () => setStep(s => Math.min(s + 1, total - 1));
  const prev = () => setStep(s => Math.max(s - 1, 0));

  const meta = STEP_META[step];
  const section = meta?.section;

  // ----------- Section intros -----------
  const SECTION_INTROS = {
    "intro-tests": {
      icon: ClipboardCheck,
      title: "Apartado 1 · Tests iniciales",
      subtitle: "Vamos a conocer tu punto de partida antes de empezar el programa.",
      description: "Completarás 6 escalas clínicas breves. Nos ayudarán a medir cómo estás ahora y, al terminar las 8 sesiones, volveremos a hacerlas para ver tu progreso.",
      bullets: [
        "PHQ-9 · Estado de ánimo (9 preguntas)",
        "GAD-7 · Ansiedad (7 preguntas)",
        "GHQ-28 · Salud general (28 preguntas)",
        "BHS · Desesperanza (20 preguntas)",
        "SWLS · Satisfacción con la vida (5 preguntas)",
        "PERMA · Bienestar general (22 preguntas)",
      ],
      buttonText: "Comenzar los tests",
    },
    "intro-objetivos": {
      icon: Sparkles,
      title: "Apartado 2 · Fijación de objetivos",
      subtitle: "Los objetivos te dan dirección y significado durante el programa.",
      description: "Vas a definir 3 metas SMART que te acompañarán durante las 8 sesiones. Después haremos una pequeña autoevaluación para asegurarnos de que están bien formuladas.",
      bullets: [
        "Específicos y positivos",
        "Medibles",
        "Alcanzables",
        "Relevantes para ti",
        "Definidos en el tiempo",
      ],
      buttonText: "Definir mis objetivos",
    },
    "intro-abc": {
      icon: Brain,
      title: "Apartado 3 · Modelo ABC",
      subtitle: "La piedra angular de la Terapia Cognitivo-Conductual.",
      description: "Aprenderás cómo tus pensamientos (B) sobre los eventos (A) determinan tus emociones y comportamientos (C). Y practicarás cómo cambiar los pensamientos para transformar cómo te sientes.",
      bullets: [
        "Teoría: cómo funciona el modelo ABC",
        "Cuerpo y mente: cómo los pensamientos afectan a tus reacciones fisiológicas",
        "Reformular: transformar pensamientos negativos en positivos",
        "Identificar: reconocer qué pensamiento produce cada emoción",
      ],
      buttonText: "Empezar el módulo ABC",
    },
  };

  if (SECTION_INTROS[meta?.id]) {
    const intro = SECTION_INTROS[meta.id];
    const Icon = intro.icon;
    return (
      <StepShell
        current={step + 1}
        total={total}
        section={section}
        onBack={prev}
        footer={
          <Button
            data-testid={`section-intro-continue-${section.id}`}
            onClick={next}
            className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-6"
          >
            {intro.buttonText} <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.75} />
          </Button>
        }
      >
        <Card className="rounded-3xl border-[var(--ha-border)] bg-white overflow-hidden" data-testid={`section-intro-${section.id}`}>
          <div className="h-2 bg-gradient-to-r from-[var(--ha-primary)] via-[var(--ha-accent)] to-[var(--ha-secondary)]" />
          <CardContent className="p-6 sm:p-10">
            <div className="flex items-start gap-4">
              <div className="h-14 w-14 rounded-2xl bg-[var(--ha-primary)]/10 grid place-items-center shrink-0">
                <Icon className="w-7 h-7 text-[var(--ha-primary)]" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[var(--ha-text)]">{intro.title}</h1>
                <p className="mt-2 text-[var(--ha-muted)]">{intro.subtitle}</p>
              </div>
            </div>

            <p className="mt-6 text-[var(--ha-text)] leading-relaxed">{intro.description}</p>

            <ul className="mt-6 space-y-2">
              {intro.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-[var(--ha-text)]">
                  <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[var(--ha-primary)] shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </StepShell>
    );
  }

  // ----------- Welcome -----------
  if (meta.id === "welcome") {
    return (
      <StepShell
        current={step + 1}
        total={total}
        title="Sesión 1 · Primeros pasos"
        subtitle="Antes de comenzar, tómate un momento para conocer el programa y comprometerte con el proceso."
      >
        <Card className="rounded-3xl border-[var(--ha-border)] bg-white">
          <CardContent className="p-6 sm:p-8">
            <p className="text-[var(--ha-muted)] leading-relaxed">
              MINDHOME HEALTH APP incluye <strong>8 sesiones</strong> de 45-60 min. El trabajo entre sesiones ayuda a interiorizar
              las herramientas. Requiere <strong>compromiso e implicación</strong>.
            </p>

            <div className="mt-6 grid sm:grid-cols-3 gap-3">
              {[
                { icon: ShieldCheck, title: "Privado", body: "Tus datos son solo tuyos." },
                { icon: Clock, title: "A tu ritmo", body: "Pausa y retoma cuando quieras." },
                { icon: HeartHandshake, title: "Basado en TCC", body: "Enfoque de eficacia probada." },
              ].map(({ icon: Icon, title, body }, i) => (
                <div key={i} className="rounded-2xl border border-[var(--ha-border)] p-4">
                  <Icon className="w-5 h-5 text-[var(--ha-primary)]" strokeWidth={1.5} />
                  <p className="mt-2 font-semibold text-[var(--ha-text)]">{title}</p>
                  <p className="text-sm text-[var(--ha-muted)]">{body}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-2xl bg-[var(--ha-primary)]/5 border border-[var(--ha-primary)]/20 p-5">
              <p className="font-semibold text-[var(--ha-text)]">¿Te gustaría comprometerte con los ejercicios para mejorar tu estado emocional?</p>
              <div className="mt-4 flex gap-3 flex-wrap">
                <Button
                  data-testid="commit-yes-btn"
                  onClick={next}
                  className="rounded-full bg-[var(--ha-primary)] hover:bg-[var(--ha-primary-hover)] text-white h-11 px-6"
                >
                  Sí, comencemos <ChevronRight className="w-4 h-4 ml-1" strokeWidth={1.75} />
                </Button>
                {resumeAvailable && (
                  <Button
                    data-testid="resume-session1-btn"
                    variant="outline"
                    onClick={() => { setStep(resumeAvailable); toast.success(`Retomando en el paso ${resumeAvailable + 1}`); }}
                    className="rounded-full border-[var(--ha-primary)] text-[var(--ha-primary)] h-11 px-6"
                  >
                    <RotateCcw className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> Continuar donde lo dejé (paso {resumeAvailable + 1})
                  </Button>
                )}
                <Button
                  data-testid="commit-no-btn"
                  variant="outline"
                  onClick={() => { toast("Sin un compromiso previo no podemos empezar. Vuelve cuando estés listo/a."); navigate("/dashboard"); }}
                  className="rounded-full border-[var(--ha-border)] h-11 px-6"
                >
                  Ahora no
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </StepShell>
    );
  }

  // ----------- Questionnaires -----------
  const q = ALL_QUESTIONNAIRES.find(x => x.kind === meta.id);
  if (q) {
    return (
      <StepShell
        current={step + 1}
        total={total}
        section={section}
        title="Evaluación inicial"
        subtitle="Estas escalas nos permiten conocer tu punto de partida. Sé lo más sincero/a posible."
        onBack={prev}
      >
        <QuestionnaireStep questionnaire={q} onDone={next} />
      </StepShell>
    );
  }

  // ----------- SMART goals -----------
  if (meta.id === "smart-goals") {
    return (
      <StepShell current={step + 1} total={total} section={section} title="Fijación de objetivos" subtitle="Vas a definir 3 metas que te acompañarán durante todo el programa." onBack={prev}>
        <SmartGoalsStep onDone={next} />
      </StepShell>
    );
  }
  if (meta.id === "smart-quiz") {
    return (
      <StepShell current={step + 1} total={total} section={section} title="Repaso rápido" onBack={prev}>
        <SmartQuizStep onDone={next} />
      </StepShell>
    );
  }
  if (meta.id === "abc-theory") {
    return (
      <StepShell current={step + 1} total={total} section={section} title="Modelo ABC" subtitle="Aprende cómo se relacionan eventos, pensamientos y consecuencias." onBack={prev}>
        <AbcTheoryStep onDone={next} />
      </StepShell>
    );
  }
  if (meta.id === "abc-physiology") {
    return (
      <StepShell current={step + 1} total={total} section={section} title="Cuerpo y mente" subtitle="Descubre cómo tus pensamientos afectan a tus reacciones corporales." onBack={prev}>
        <AbcPhysiologyStep onDone={next} />
      </StepShell>
    );
  }
  if (meta.id === "abc-reframe") {
    return (
      <StepShell current={step + 1} total={total} section={section} title="Reformula tus pensamientos" onBack={prev}>
        <AbcReframeStep onDone={next} />
      </StepShell>
    );
  }
  if (meta.id === "abc-identify") {
    return (
      <StepShell current={step + 1} total={total} section={section} title="Identifica el pensamiento" onBack={prev}>
        <AbcIdentifyStep onDone={next} />
      </StepShell>
    );
  }
  if (meta.id === "summary") {
    return (
      <StepShell current={step + 1} total={total} onBack={prev}>
        <SummaryStep />
      </StepShell>
    );
  }
  return null;
}
